import PocketBase, { LocalAuthStore } from 'pocketbase';

// Same-origin paths: Caddy serves the SPA and proxies /pb and /api.
// Vite dev proxies them to the local services (see vite.config.js).
const pocketBaseUrl = import.meta.env.VITE_PB_URL || '/pb';
export { pocketBaseUrl };

// ------------------------------------------------------------------
// Triple auth stores (E5/E1). Admin, client, and engineer sessions live
// in SEPARATE PocketBase clients + storage keys so opening a portal or
// engineer link no longer clobbers the admin session (and vice versa).
// `pb` stays the admin client (default export) — AdminShell/Login use it
// directly.
// ------------------------------------------------------------------
export const pb = new PocketBase(pocketBaseUrl, new LocalAuthStore('pp_admin'));
export const pbPortal = new PocketBase(pocketBaseUrl, new LocalAuthStore('pp_portal'));
export const pbEngineer = new PocketBase(pocketBaseUrl, new LocalAuthStore('pp_engineer'));
pb.autoCancellation(false);
pbPortal.autoCancellation(false);
pbEngineer.autoCancellation(false);

// The "active" client is whichever portal the current route belongs to
// (/portal/* → pbPortal, everything else → pb). DataProvider targets the
// active client; the scope flips via App's route watcher.
let activeClient = pb;
const scopeListeners = new Set();

export function activePb() {
  return activeClient;
}

export function setActivePb(client) {
  if (activeClient === client) return false;
  activeClient = client;
  scopeListeners.forEach((fn) => {
    try {
      fn();
    } catch {
      // listener errors must not break the scope flip
    }
  });
  return true;
}

export function onScopeChange(fn) {
  scopeListeners.add(fn);
  return () => scopeListeners.delete(fn);
}

// ------------------------------------------------------------------
// Protected files (documents, photos, invoice PDFs) are served only
// with a short-lived file token. Cache per client (admin/portal tokens
// differ); refresh lazily. ⚠ PocketBase's file-token duration is ~2
// MINUTES (not 3 hours — an earlier comment got this wrong and caused
// "View" links to 404 minutes after page load). The cache window stays
// well under that; anything longer-lived must mint fresh at click time
// via openProtectedFile()/protectedFileUrl() instead of baking URLs
// into state.
// ------------------------------------------------------------------
const fileTokenCaches = new WeakMap();
const FILE_TOKEN_CACHE_MS = 90 * 1000; // safe margin under PB's ~2min TTL

export async function getFileToken() {
  const client = activePb();
  const cached = fileTokenCaches.get(client);
  if (cached && cached.expiresAt > Date.now() + 10 * 1000) {
    return cached.token;
  }
  const token = await client.files.getToken();
  fileTokenCaches.set(client, {
    token,
    expiresAt: Date.now() + FILE_TOKEN_CACHE_MS,
  });
  return token;
}

export function clearFileToken(client = activePb()) {
  fileTokenCaches.delete(client);
}

// ------------------------------------------------------------------
// Fresh-at-click-time protected file access. `recordStub` only needs
// `{ id, collectionName }` — the SDK builds the URL from those, so a
// UI-shaped record carrying the id is enough.
// ------------------------------------------------------------------
export async function protectedFileUrl(recordStub, filename, { thumb, download } = {}) {
  if (!recordStub?.id || !filename) return '';
  const token = await getFileToken();
  let url = activePb().files.getURL(
    { id: recordStub.id, collectionName: recordStub.collectionName || 'documents' },
    filename,
    thumb ? { thumb, token } : { token },
  );
  // PB's file endpoint honours ?download=1 with an attachment disposition
  // (audit.pb.js reads it to distinguish `download` from `view` events).
  if (download) url += (url.includes('?') ? '&' : '?') + 'download=1';
  return url;
}

export async function openProtectedFile(recordStub, filename) {
  const url = await protectedFileUrl(recordStub, filename);
  if (url) window.open(url, '_blank', 'noopener');
}

// Explicit download: same token flow as openProtectedFile but with
// download=1 so PB serves attachment disposition (and the audit hook logs
// a `download` event — tracking_tasks.md TRK-3.2).
export async function downloadProtectedFile(recordStub, filename) {
  const url = await protectedFileUrl(recordStub, filename, { download: true });
  if (!url) return;
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

// ------------------------------------------------------------------
// Session keep-alive (issue #1: "logged out every time we leave the
// screen"). Tokens now last 30 days (migration 002) but PB does not
// slide the window on its own — refresh quietly on app start so an
// active user is never bounced by an expired token mid-session. Both
// stores are refreshed independently; a dead token clears only that
// store so gates show the right login screen.
// ------------------------------------------------------------------
async function refreshStore(client) {
  if (!client.authStore.token || !client.authStore.record) return;
  try {
    await client.collection('accounts').authRefresh();
  } catch (err) {
    if (!client.authStore.isValid) {
      console.warn('[pb] session expired — clearing auth store');
      client.authStore.clear();
      clearFileToken(client);
    }
    // Offline / transient network failure: keep the store, retry later.
  }
}

export async function keepSessionAlive() {
  await Promise.allSettled([refreshStore(pb), refreshStore(pbPortal), refreshStore(pbEngineer)]);
}

export function tenantFilter(tenantId, extraFilter = '') {
  const tenantClause = `tenant_id="${tenantId}"`;
  return extraFilter ? `${tenantClause} && (${extraFilter})` : tenantClause;
}

// Resolves to a real UnsubscribeFunc (SDK 0.28 subscribe() is async and
// its return value must NEVER be called directly — see DataProvider).
export async function subscribeToMessages(clientId, handler) {
  const client = activePb();
  const topic = clientId ? `client_id="${clientId}"` : '*';
  await client.collection('messages').subscribe(topic, handler);
  return () => client.collection('messages').unsubscribe(topic);
}

export default pb;
